import { useEffect, useState } from 'react';
import {
    PanelBody,
    Button,
    Modal,
    TextControl,
    SelectControl,
    TextareaControl,
    Spinner,
    CheckboxControl,
} from '@wordpress/components';
import { __ } from '@wordpress/i18n';
import { dispatch } from '@wordpress/data';

const FieldsTab = () => {
    const PRO_URL = 'https://example.com/your-pro-version'; // <-- update to your real pro URL
    const PRO_TYPES = ['date', 'select', 'checkbox'];
    const [isOpen, setIsOpen] = useState(false);
    const [fieldTitle, setFieldTitle] = useState('');
    const [fieldPlaceholder, setFieldPlaceholder] = useState('Please Select');
    const [fieldType, setFieldType] = useState('text');
    const [fieldLocation, setFieldLocation] = useState('contact');
    const [fieldOptions, setFieldOptions] = useState('');
    const [fieldRequired, setFieldRequired] = useState(false);
    const [fieldDateFormat, setFieldDateFormat] = useState('yy-mm-dd');
    const [isSaving, setIsSaving] = useState(false);
    const [fields, setFields] = useState([]); // Store existing fields
    const [editingFieldId, setEditingFieldId] = useState(null); // For editing
    const [loading, setLoading] = useState(true);
    const [isDeleting, setIsDeleting] = useState(false); // State for delete loading
    const [messageModalOpen, setMessageModalOpen] = useState(false);
    const [messageModalType, setMessageModalType] = useState('success');
    const [messageModalMessage, setMessageModalMessage] = useState('');
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [deleteTargetId, setDeleteTargetId] = useState(null);

    useEffect(() => {
        fetchFields(); // Fetch existing fields on mount
    }, []);

    const fetchFields = () => {
        setLoading(true);
         fetch(wp_ajax.ajax_url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
            },
            body: new URLSearchParams({
                action: 'get_all_fields',
                security: wp_ajax.nonce // Include the nonce for security
            })
        })
        .then(response => response.json())
        .then(data => {
            setLoading(false);
            if (data.success && Array.isArray(data.data)) {
                setFields(data.data); // Set fetched fields
            } else {
                console.error('Response data is not an array:', data);
            }
        })
        .catch(error => {
            setLoading(false);
            console.error('Error fetching fields:', error);
        });
    };

    const openModal = (field = null) => {
        setIsOpen(true);
        if (field) {
            setEditingFieldId(field.id);
            setFieldTitle(field.title);
            setFieldType(field.type);
            setFieldLocation(field.location);
            setFieldOptions(field.options);
            setFieldRequired(field.required === true || field.required === 1 || field.required === '1');
            setFieldDateFormat(field.dateformat || 'yy-mm-dd');
        } else {
            resetForm(); // Reset for new field
        }
    };

    const closeModal = () => {
        setIsOpen(false);
        resetForm(); // Reset the form on close
    };

    const resetForm = () => {
        setFieldTitle('');
        setFieldType('text');
        setFieldLocation('contact');
        setFieldOptions('');
        setFieldRequired(false);
        setFieldDateFormat('yy-mm-dd');
        setEditingFieldId(null);
    };

    const saveField = () => {
        setIsSaving(true);
        const data = {
            title: fieldTitle,
            type: fieldType,
            location: fieldLocation,
            placeholder: fieldPlaceholder,
            options: fieldOptions,
            required: fieldRequired,
            dateformat: fieldDateFormat,
            id: editingFieldId, // Include ID if editing
        };

        fetch(wp_ajax.ajax_url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
            },
            body: new URLSearchParams({
                action: 'save_field_data', // Action name in your PHP handler
                'field_data[title]': data.title,
                'field_data[type]': data.type,
                'field_data[location]': data.location,
                'field_data[options]': data.options,
                'field_data[placeholder]': data.placeholder,
                'field_data[required]': data.required ? '1' : '0',
                'field_data[dateformat]': data.dateformat,
                'field_data[id]': data.id, // Include ID if editing
                security: wp_ajax.nonce, // Security nonce
            }),
        })
        .then(response => response.json())
        .then((response) => {
                if (response.success) {
                    setMessageModalType('success');
                    setMessageModalMessage(__('Field saved successfully!', 'custom-fields-checkout-block-for-woocommerce'));
                    setMessageModalOpen(true);
                    fetchFields(); // Refresh the field list
                    closeModal();
                } else {
                    console.error('Error saving field:', response.data);
                    setMessageModalType('error');
                    setMessageModalMessage(__('Error saving field.', 'custom-fields-checkout-block-for-woocommerce'));
                    setMessageModalOpen(true);
                }
        })
            .catch((error) => {
                console.error('Error saving field:', error);
                setMessageModalType('error');
                setMessageModalMessage(__('Error saving field.', 'custom-fields-checkout-block-for-woocommerce'));
                setMessageModalOpen(true);
            })
        .finally(() => {
            setIsSaving(false);
        });
    };


    const deleteField = (fieldId) => {
        setDeleteTargetId(fieldId);
        setConfirmDeleteOpen(true);
    };

    const performDelete = (fieldId) => {
        setIsDeleting(true);
        fetch(wp_ajax.ajax_url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
            },
            body: new URLSearchParams({
                action: 'delete_field_data', // Action name in PHP handler
                id: fieldId, // Field ID to delete
                security: wp_ajax.nonce, // Security nonce
            }),
        })
        .then(response => response.json())
        .then((response) => {
            if (response.success) {
                setMessageModalType('success');
                setMessageModalMessage(__('Field deleted successfully!', 'custom-fields-checkout-block-for-woocommerce'));
                setMessageModalOpen(true);
                fetchFields(); // Refresh the field list
            } else {
                console.error('Error deleting field:', response.data);
                setMessageModalType('error');
                setMessageModalMessage(__('Failed to delete field.', 'custom-fields-checkout-block-for-woocommerce'));
                setMessageModalOpen(true);
            }
        })
        .catch((error) => {
            console.error('Error deleting field:', error);
            setMessageModalType('error');
            setMessageModalMessage(__('Failed to delete field.', 'custom-fields-checkout-block-for-woocommerce'));
            setMessageModalOpen(true);
        })
        .finally(() => {
            setIsDeleting(false);
            setConfirmDeleteOpen(false);
            setDeleteTargetId(null);
        });
    };


    return (
        <PanelBody>
            <div>
                <Button isPrimary onClick={() => openModal()}>
                    Add Field
                </Button>
            </div>
            {loading ? ( // Conditional rendering based on loading state
                <Spinner />
            ) : (
                <>
                    <div className="cfcblist">
                        <table className="wp-list-table widefat fixed striped table-view-list">
                            <thead>
                                <tr>
                                    <th>{__('Field Title', 'custom-fields-checkout-block-for-woocommerce')}</th>
                                    <th>{__('Field Type', 'custom-fields-checkout-block-for-woocommerce')}</th>
                                    <th>{__('Field Key', 'custom-fields-checkout-block-for-woocommerce')}</th>
                                    <th>{__('Field Location', 'custom-fields-checkout-block-for-woocommerce')}</th>
                                    <th>{__('Action', 'custom-fields-checkout-block-for-woocommerce')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {fields.length > 0 ? ( // Check if fields exist
                                    fields.map((field) => (
                                        <tr key={field.id}>
                                            <td>{field.title}</td>
                                            <td>{field.type}</td>
                                            <td><code>{`cfcbw/field${field.id}`}</code></td>
                                            <td>{field.location}</td>
                                            <td>
                                                <Button isPrimary onClick={() => openModal(field)}>
                                                    Edit
                                                </Button>
                                                <Button
                                                    isDestructive
                                                    onClick={() => deleteField(field.id)}
                                                    disabled={isDeleting} // Disable button if deleting
                                                >
                                                    {isDeleting ? <Spinner size="small" /> : __('Delete', 'custom-fields-checkout-block-for-woocommerce')}
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="4" style={{ textAlign: 'center' }}>
                                            {__('No fields available.', 'custom-fields-checkout-block-for-woocommerce')}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
            {isOpen && (
                <Modal
                    title={__('Add/Edit Field', 'custom-fields-checkout-block-for-woocommerce')}
                    onRequestClose={closeModal}
                >
                    <div>
                        <TextControl
                            label={__('Field Title', 'custom-fields-checkout-block-for-woocommerce')}
                            value={fieldTitle}
                            onChange={(value) => setFieldTitle(value)}
                            placeholder={__('Enter field title', 'custom-fields-checkout-block-for-woocommerce')}
                        />
                        <SelectControl
                            label={__('Field Type', 'custom-fields-checkout-block-for-woocommerce')}
                            value={fieldType}
                            options={[
                                { label: __('Text', 'custom-fields-checkout-block-for-woocommerce'), value: 'text' },
                                { label: __('Email', 'custom-fields-checkout-block-for-woocommerce'), value: 'email' },
                                { label: __('Url', 'custom-fields-checkout-block-for-woocommerce'), value: 'url' },
                                { label: __('Date Picker (Pro)', 'custom-fields-checkout-block-for-woocommerce'), value: 'date', disabled: true },
                                { label: __('Select (Pro)', 'custom-fields-checkout-block-for-woocommerce'), value: 'select', disabled: true },
                                { label: __('Checkbox (Pro)', 'custom-fields-checkout-block-for-woocommerce'), value: 'checkbox', disabled: true },
                            ]}
                            onChange={(value) => {
                                // Prevent selecting Pro-only types in the free version
                                if (PRO_TYPES.includes(value)) {
                                    // open pro link in new tab
                                    window.open(PRO_URL, '_blank');
                                    return;
                                }
                                setFieldType(value);
                            }}
                        />

                        <div style={{ marginTop: 8 }}>
                            <small>
                                {__('Date Picker, Select and Checkbox are available in the Pro version.', 'custom-fields-checkout-block-for-woocommerce')}
                                {' '}
                                <a href={PRO_URL} target="_blank" rel="noreferrer">{__('Get Pro version', 'custom-fields-checkout-block-for-woocommerce')}</a>
                            </small>
                        </div>
                        <SelectControl
                            label={__('Location', 'custom-fields-checkout-block-for-woocommerce')}
                            value={fieldLocation}
                            options={[
                                { label: __('Contact', 'custom-fields-checkout-block-for-woocommerce'), value: 'contact' },
                                { label: __('Address', 'custom-fields-checkout-block-for-woocommerce'), value: 'address' },
                                { label: __('Order', 'custom-fields-checkout-block-for-woocommerce'), value: 'order' },
                            ]}
                            onChange={(value) => setFieldLocation(value)}
                        />
                        <CheckboxControl
                            label={__('Required', 'custom-fields-checkout-block-for-woocommerce')}
                            checked={fieldRequired}
                            onChange={(value) => setFieldRequired(value)}
                        />
                        {(fieldType === 'select') && (
                            <>
                            <TextControl
                                label={__('Field Placeholder', 'custom-fields-checkout-block-for-woocommerce')}
                                value={fieldPlaceholder}
                                onChange={(value) => setFieldPlaceholder(value)}
                                placeholder={__('Enter field placeholder', 'custom-fields-checkout-block-for-woocommerce')}
                            />

                            <TextareaControl
                                label={__('Options', 'custom-fields-checkout-block-for-woocommerce')}
                                value={fieldOptions}
                                onChange={(value) => setFieldOptions(value)}
                                placeholder={__('Enter options for field', 'custom-fields-checkout-block-for-woocommerce')}
                            />
                            </>
                        )}
                        {(fieldType === 'date') && (
                            <TextControl
                                label={__('Date Format', 'custom-fields-checkout-block-for-woocommerce')}
                                value={fieldDateFormat}
                                onChange={(value) => setFieldDateFormat(value)}
                                help={__('jQuery UI Datepicker format, e.g. yy-mm-dd, mm/dd/yy, dd-mm-yy', 'custom-fields-checkout-block-for-woocommerce')}
                                placeholder={__('yy-mm-dd', 'custom-fields-checkout-block-for-woocommerce')}
                            />
                        )}
                    </div>
                    <div>
                        <Button isPrimary onClick={saveField} disabled={isSaving}>
                            {isSaving ? __('Saving...', 'custom-fields-checkout-block-for-woocommerce') : __('Save Field', 'custom-fields-checkout-block-for-woocommerce')}
                        </Button>
                        <Button isSecondary onClick={closeModal}>
                            {__('Cancel', 'custom-fields-checkout-block-for-woocommerce')}
                        </Button>
                    </div>
                </Modal>
            )}
            {confirmDeleteOpen && (
                <Modal
                    title={__('Confirm Delete', 'custom-fields-checkout-block-for-woocommerce')}
                    onRequestClose={() => setConfirmDeleteOpen(false)}
                >
                    <p>{__('Are you sure you want to delete this field?', 'custom-fields-checkout-block-for-woocommerce')}</p>
                    <div>
                        <Button isSecondary onClick={() => setConfirmDeleteOpen(false)}>{__('Cancel', 'custom-fields-checkout-block-for-woocommerce')}</Button>
                        <Button isDestructive onClick={() => performDelete(deleteTargetId)} disabled={isDeleting}>
                            {isDeleting ? <Spinner size="small" /> : __('Delete', 'custom-fields-checkout-block-for-woocommerce')}
                        </Button>
                    </div>
                </Modal>
            )}
            {messageModalOpen && (
                <Modal
                    title={messageModalType === 'success' ? __('Success', 'custom-fields-checkout-block-for-woocommerce') : __('Error', 'custom-fields-checkout-block-for-woocommerce')}
                    onRequestClose={() => setMessageModalOpen(false)}
                >
                    <p>{messageModalMessage}</p>
                    <div>
                        <Button isPrimary onClick={() => setMessageModalOpen(false)}>{__('OK', 'custom-fields-checkout-block-for-woocommerce')}</Button>
                    </div>
                </Modal>
            )}
        </PanelBody>
    );
};

export default FieldsTab;
